import { useMemo } from "preact/hooks";
import fuzzysort from "fuzzysort";
import type { Category } from "../types";
import { useEdition } from "../hooks/useEdition";
import { useTranslations } from "../hooks/useTranslations";
import { useEvents, useSchedules } from "../hooks/useWidgetQueries";
import { useNavigation } from "../lib/router";
import { useUrlStringState, useUrlArrayState } from "../hooks/useUrlState";
import { getDisplayDate } from "../lib/dateUtils";
import { getLocalizedName } from "../lib/localization";
import { hasAnySelectedCategory, matchesConfiguredCategory } from "../lib/categoryFilter";
import { EventCard } from "../components/events/EventCard";
import { EmptyState } from "../components/ui/EmptyState";
import { FilterDropdown } from "../components/ui/FilterDropdown";
import { SearchInput } from "../components/ui/SearchInput";
import { DaySelector } from "../components/ui/DaySelector";
import {
  ArtistCardSkeleton,
  DaySelectorSkeleton,
  FilterDropdownSkeleton,
} from "../components/ui/Skeleton";

type EventsPageProps = {
  onNavigate: (path: string, elementId?: string) => void;
};

export function EventsPage({ onNavigate }: EventsPageProps) {
  const {
    edition,
    editionId,
    locale,
    lockedCategoryIds,
    categoryName: configCategoryName,
  } = useEdition();
  const t = useTranslations();
  const { toEvent } = useNavigation();
  const { data: events, loading } = useEvents(editionId);
  const { data: schedules } = useSchedules(editionId);

  const [search, setSearch] = useUrlStringState("search", "");
  const [selectedDay, setSelectedDay] = useUrlStringState("day", null);
  const [selectedCategories, setSelectedCategories] = useUrlArrayState(
    "categories",
    [],
  );

  const eventDisplayDays = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!schedules) return map;

    for (const schedule of schedules) {
      if (!schedule.event?._id) continue;

      const displayDate = getDisplayDate(
        schedule.start_date,
        schedule.start_time,
        edition?.day_start_time ?? null,
        edition?.start_date ?? null,
      );

      let daySet = map.get(schedule.event._id);
      if (!daySet) {
        daySet = new Set<string>();
        map.set(schedule.event._id, daySet);
      }
      daySet.add(displayDate);
    }

    return map;
  }, [schedules, edition]);

  const days = useMemo(() => {
    if (!events) return [];

    const scheduledDays = new Set<string>();

    for (const event of events) {
      if (lockedCategoryIds.length > 0) {
        if (!hasAnySelectedCategory(event.categories, lockedCategoryIds)) {
          continue;
        }
      } else if (configCategoryName) {
        if (
          !event.categories.some((cat) =>
            matchesConfiguredCategory(
              cat,
              configCategoryName,
              locale,
              edition?.default_locale_code
            )
          )
        ) {
          continue;
        }
      }

      const eventDays = eventDisplayDays.get(event._id);
      if (!eventDays) continue;

      for (const day of eventDays) {
        scheduledDays.add(day);
      }
    }

    return Array.from(scheduledDays).sort();
  }, [events, eventDisplayDays, lockedCategoryIds, configCategoryName, locale, edition]);

  const activeDay = selectedDay && days.includes(selectedDay) ? selectedDay : null;

  const categories = useMemo(() => {
    if (!events) return [];
    const catMap = new Map<string, Category>();
    for (const event of events) {
      for (const cat of event.categories) {
        if (!catMap.has(cat._id)) {
          catMap.set(cat._id, cat as Category);
        }
      }
    }
    return Array.from(catMap.values()).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
  }, [events]);

  const categoryOptions = useMemo(() => {
    return categories.map((cat) => ({
      id: cat._id,
      label:
        getLocalizedName(cat, locale, edition?.default_locale_code) ??
        cat.name ??
        "",
    }));
  }, [categories, locale, edition?.default_locale_code]);

  const filteredEvents = useMemo(() => {
    if (!events) return [];

    let filtered = events.filter((event) => {
      if (lockedCategoryIds.length > 0) {
        if (!hasAnySelectedCategory(event.categories, lockedCategoryIds)) {
          return false;
        }
      } else if (configCategoryName) {
        // Legacy fallback for older blocks/shortcodes using category name
        if (
          !event.categories.some(
            (cat) =>
              matchesConfiguredCategory(
                cat,
                configCategoryName,
                locale,
                edition?.default_locale_code
              ),
          )
        ) {
          return false;
        }
      } else if (selectedCategories.length > 0) {
        if (!hasAnySelectedCategory(event.categories, selectedCategories)) {
          return false;
        }
      }

      if (activeDay !== null) {
        const eventDays = eventDisplayDays.get(event._id);
        if (!eventDays?.has(activeDay)) {
          return false;
        }
      }

      return true;
    });

    if (search) {
      const results = fuzzysort.go(search, filtered, {
        key: (event) =>
          getLocalizedName(event, locale, edition?.default_locale_code) ?? "",
        threshold: -10000,
      });
      filtered = results.map((result) => result.obj);
    }

    return filtered;
  }, [
    events,
    lockedCategoryIds,
    configCategoryName,
    selectedCategories,
    activeDay,
    eventDisplayDays,
    search,
    locale,
    edition,
  ]);

  const hasActiveFilters =
    (search && search.length > 0) ||
    activeDay !== null ||
    selectedCategories.length > 0;

  const clearFilters = () => {
    setSearch(null);
    setSelectedDay(null);
    setSelectedCategories([]);
  };

  if (loading) {
    return (
      <div className="festapp-artists-page">
        <div className="festapp-artists-filters">
          <SearchInput value="" onChange={() => {}} placeholder={t.searchEvents} disabled />
          <div className="festapp-artists-filter-row">
            <DaySelectorSkeleton />
            <FilterDropdownSkeleton />
          </div>
        </div>
        <div className="festapp-artists-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <ArtistCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="festapp-artists-page">
      <div className="festapp-artists-filters">
        <SearchInput
          value={search ?? ""}
          onChange={(v) => setSearch(v || null)}
          placeholder={t.searchEvents}
        />
        <div className="festapp-artists-filter-row">
          <DaySelector
            days={days}
            selectedDay={activeDay}
            onChange={setSelectedDay}
          />
          {categories.length > 0 && lockedCategoryIds.length === 0 && !configCategoryName && (
            <FilterDropdown
              label={t.categories}
              options={categoryOptions}
              selected={selectedCategories}
              onChange={setSelectedCategories}
            />
          )}
          {hasActiveFilters && (
            <button
              type="button"
              className="festapp-btn festapp-btn-ghost festapp-btn-sm"
              onClick={clearFilters}
            >
              {t.clearFilters}
            </button>
          )}
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <EmptyState
          message={t.noEventsFound}
          onClearFilters={hasActiveFilters ? clearFilters : undefined}
        />
      ) : (
        <div className="festapp-artists-grid">
          {filteredEvents.map((event) => (
            <EventCard
              key={event._id}
              event={event}
              onClick={() => onNavigate(toEvent(event._id), event._id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
